import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Card, Notice, Spinner } from '../components/Shell';
import { Stamp } from '../components/Stamp';
import type { AgreementForSigning } from '../lib/types';

/**
 * The public signing page. No account, no password: the link token is the
 * credential, exactly as an emailed contract link works elsewhere.
 *
 * The paper pack asks for initials on every page and one signature at the end.
 * That structure is kept deliberately, because it is what makes each clause a
 * thing the client actually passed their eyes over rather than one blanket
 * tick at the bottom.
 */
export function SignAgreement() {
  const { token } = useParams<{ token: string }>();

  const [data, setData] = useState<AgreementForSigning | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [initials, setInitials] = useState<Record<string, string>>({});
  const [consents, setConsents] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<'type' | 'draw'>('type');
  const [typedName, setTypedName] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (!token) return;
    void api<AgreementForSigning>(`/public/agreements/${token}`, { anonymous: true })
      .then((result) => {
        setData(result);
        setTypedName(result.signer.fullName);
        if (result.agreement.status === 'signed') setSigned(true);
      })
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'This link could not be opened.'));
  }, [token]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!token || !data) return;

    setBusy(true);
    setSubmitError(null);
    setFieldErrors({});

    void (async () => {
      try {
        await api(`/public/agreements/${token}/sign`, {
          method: 'POST',
          anonymous: true,
          body: {
            signatureMethod: method,
            typedName: method === 'type' ? typedName : undefined,
            signatureImage: method === 'draw' ? signatureData : undefined,
            initials,
            acceptedConsents: [...consents],
          },
        });
        setSigned(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        if (err instanceof ApiError) {
          setFieldErrors(err.fieldErrors ?? {});
          setSubmitError(err.fieldErrors ? 'Some parts still need your attention.' : err.message);
        } else {
          setSubmitError('The signature did not save. Try again.');
        }
      } finally {
        setBusy(false);
      }
    })();
  };

  if (loadError) {
    return (
      <div className="mx-auto max-w-lg px-5 py-24">
        <h1 className="text-3xl">This link will not open</h1>
        <p className="mt-3 text-[color:var(--color-ink-soft)]">{loadError}</p>
        <p className="mt-3 text-sm text-[color:var(--color-ink-soft)]">
          Signing links expire after 30 days. Ask your sitter to send a new one.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-lg px-5 py-24">
        <Spinner label="Opening the agreement" />
      </div>
    );
  }

  if (signed) {
    return (
      <div className="mx-auto max-w-lg px-5 py-24 text-center">
        <div className="mb-6 flex justify-center">
          <Stamp tone="ok">Agreement signed</Stamp>
        </div>
        <h1 className="text-3xl">That is everything</h1>
        <p className="mt-4 text-[color:var(--color-ink-soft)]">
          Your {data.agreement.season} agreement with {data.business.businessName} is signed and on
          file. We emailed you a copy, and it is always available in your portal.
        </p>
        <p className="mt-8 font-mono text-xs text-[color:var(--color-ink-faint)]">
          Questions? {data.business.publicEmail}
        </p>
      </div>
    );
  }

  const initialledCount = data.template.sections.filter(
    (s) => s.requiresInitials && initials[s.sectionId]?.trim()
  ).length;
  const initialsNeeded = data.template.sections.filter((s) => s.requiresInitials).length;

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24">
      <header className="border-b border-[color:var(--color-rule)] py-10">
        <p className="eyebrow mb-3">{data.business.businessName}</p>
        <h1 className="text-3xl sm:text-4xl">{data.template.name}</h1>
        <p className="mt-4 text-[color:var(--color-ink-soft)]">
          For {data.signer.fullName}. Read each section and put your initials in the box beside it,
          then sign at the bottom. It takes about five minutes.
        </p>
      </header>

      {/* A live count, so the progress through a long document is legible. */}
      <div className="sticky top-0 z-10 -mx-5 mb-8 border-b border-[color:var(--color-rule)] bg-[color:var(--color-sheet)]/95 px-5 py-3 backdrop-blur">
        <p className="font-mono text-xs tracking-wider text-[color:var(--color-ink-soft)]">
          {initialledCount} of {initialsNeeded} sections initialled
        </p>
        <div
          className="mt-1.5 h-1 w-full bg-[color:var(--color-sheet-edge)]"
          role="progressbar"
          aria-valuenow={initialledCount}
          aria-valuemin={0}
          aria-valuemax={initialsNeeded}
          aria-label="Sections initialled"
        >
          <div
            className="h-full bg-[color:var(--color-stamp)] transition-[width] duration-300"
            style={{ width: `${initialsNeeded ? (initialledCount / initialsNeeded) * 100 : 0}%` }}
          />
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <div className="space-y-6">
          {data.template.sections.map((section, index) => (
            <Card
              key={section.sectionId}
              tint={index % 2 === 0 ? 'white' : 'yellow'}
              className="p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {section.heading && (
                    <h2 className="font-display text-xl font-bold">{section.heading}</h2>
                  )}
                  <div className="mt-3 space-y-3 text-[0.94rem] leading-relaxed">
                    {section.body.split(/\n{2,}/).map((paragraph, i) => (
                      <p key={i}>{paragraph.trim()}</p>
                    ))}
                  </div>
                </div>

                {section.requiresInitials && (
                  <label className="w-24 shrink-0">
                    <span className="field-label">Initials</span>
                    <input
                      className="w-full border-2 border-[color:var(--color-rule)] bg-white px-2 py-2 text-center font-mono text-lg uppercase focus:border-[color:var(--color-stamp)] focus:outline-none"
                      value={initials[section.sectionId] ?? ''}
                      onChange={(e) =>
                        setInitials((prev) => ({
                          ...prev,
                          [section.sectionId]: e.target.value.toUpperCase().slice(0, 4),
                        }))
                      }
                      maxLength={4}
                      aria-label={`Initials for ${section.heading ?? 'this section'}`}
                      aria-invalid={fieldErrors[`initials.${section.sectionId}`] ? 'true' : undefined}
                    />
                    {fieldErrors[`initials.${section.sectionId}`] && (
                      <span className="field-error">{fieldErrors[`initials.${section.sectionId}`]}</span>
                    )}
                  </label>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* --- Consents ---------------------------------------------------- */}
        {data.template.consents.length > 0 && (
          <Card tint="pink" className="mt-6 p-6">
            <h2 className="font-display text-xl font-bold">Your permissions</h2>
            <div className="mt-4 space-y-4">
              {data.template.consents.map((consent) => (
                <label key={consent.consentId} className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 shrink-0 accent-[color:var(--color-stamp)]"
                    checked={consents.has(consent.consentId)}
                    onChange={(e) =>
                      setConsents((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(consent.consentId);
                        else next.delete(consent.consentId);
                        return next;
                      })
                    }
                  />
                  <span className="text-sm leading-relaxed">
                    {consent.label}
                    {consent.required && (
                      <span className="ml-2 font-mono text-xs text-[color:var(--color-flag)]">Required</span>
                    )}
                    {fieldErrors[`consents.${consent.consentId}`] && (
                      <span className="field-error">{fieldErrors[`consents.${consent.consentId}`]}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </Card>
        )}

        {/* --- Signature ---------------------------------------------------- */}
        <Card tab="Signature" className="mt-10 p-6">
          <div className="mb-5 flex gap-2">
            {(['type', 'draw'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setMethod(option)}
                aria-pressed={method === option}
                className={`rounded-sm border px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
                  method === option
                    ? 'border-[color:var(--color-stamp)] bg-[color:var(--color-stamp)] text-white'
                    : 'border-[color:var(--color-rule)] text-[color:var(--color-ink-soft)]'
                }`}
              >
                {option === 'type' ? 'Type my name' : 'Draw it'}
              </button>
            ))}
          </div>

          {method === 'type' ? (
            <label className="block">
              <span className="field-label">Type your full name to sign</span>
              <input
                className="w-full border-b-2 border-[color:var(--color-rule)] bg-transparent py-3 font-display text-3xl italic focus:border-[color:var(--color-stamp)] focus:outline-none"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                aria-invalid={fieldErrors.typedName ? 'true' : undefined}
                autoComplete="name"
              />
              {fieldErrors.typedName && <span className="field-error">{fieldErrors.typedName}</span>}
              <span className="mt-2 block text-xs text-[color:var(--color-ink-soft)]">
                Must match the name on your profile: {data.signer.fullName}
              </span>
            </label>
          ) : (
            <SignaturePad value={signatureData} onChange={setSignatureData} error={fieldErrors.signatureImage} />
          )}

          <p className="mt-6 text-xs leading-relaxed text-[color:var(--color-ink-soft)]">
            Signing records the date, time and your network address alongside version{' '}
            {data.template.version} of this agreement. You will be emailed a PDF copy.
          </p>

          {submitError && (
            <div className="mt-5">
              <Notice tone="error">{submitError}</Notice>
            </div>
          )}

          <button type="submit" className="btn btn-primary mt-6 w-full" disabled={busy}>
            {busy ? 'Signing' : `Sign the ${data.agreement.season} agreement`}
          </button>
        </Card>
      </form>
    </div>
  );
}

/**
 * A canvas signature pad. Pointer events cover mouse, touch and stylus in one
 * path, which matters because most people sign this on a phone.
 */
function SignaturePad({
  value,
  onChange,
  error,
}: {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  error?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const setUpCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;

    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    // A white ground, so the exported PNG is not transparent in the PDF.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    context.lineWidth = 2.2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#1e2320';
  }, []);

  useEffect(() => {
    setUpCanvas();
    window.addEventListener('resize', setUpCanvas);
    return () => window.removeEventListener('resize', setUpCanvas);
  }, [setUpCanvas]);

  const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    drawing.current = true;
    const { x, y } = pointFrom(event);
    context.beginPath();
    context.moveTo(x, y);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;
    const { x, y } = pointFrom(event);
    context.lineTo(x, y);
    context.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (canvas && hasInk) onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    setUpCanvas();
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <span className="field-label">Sign in the box</span>
      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        className="h-40 w-full touch-none rounded-sm border-2 border-[color:var(--color-rule)] bg-white"
        aria-label="Signature drawing area"
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-[color:var(--color-ink-soft)]">
          {value ? 'Signature captured.' : 'Use your finger, a stylus or the mouse.'}
        </span>
        <button type="button" className="btn btn-quiet" onClick={clear}>
          Clear
        </button>
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
